import { Trans, useTranslation } from 'react-i18next'
import { Form } from 'react-router'
import { Button } from './ui/button.tsx'
import { Icon } from './ui/icon.tsx'

/**
 * The support-session frame (EPT-108): while an admin is impersonating, a
 * persistent warning-toned strip sits above every AppShell navbar naming the
 * impersonated user and offering Stop. App-level chrome (like AppShell), not a
 * `ui/*` primitive — no design-sync lockstep. SSR-fed from the root loader's
 * impersonator: `name` is the impersonated user's display name, `null` when the
 * request is its own user, so the banner renders nothing.
 *
 * A11y: a single `role="status"` region. It is SSR-present on every page and
 * stays mounted across client navigations, so it is announced once when
 * impersonation begins, not re-announced per navigation.
 */
export function ImpersonationBanner({ name }: { name: string | null }) {
	const { t } = useTranslation('common')
	if (!name) return null
	return (
		<div
			role="status"
			className="bg-warning-soft border-warning-border text-warning-fg flex items-center justify-between gap-4 border-b px-4 py-2"
		>
			<p className="text-body-sm flex min-w-0 flex-1 items-start gap-2">
				<Icon
					name="warning-triangle"
					className="size-4 shrink-0 translate-y-0.5"
					aria-hidden
				/>
				<span className="min-w-0">
					<Trans
						t={t}
						i18nKey="impersonation.banner"
						values={{ name }}
						components={{ 1: <strong className="font-semibold" /> }}
					/>
				</span>
			</p>
			<Form
				method="post"
				action="/resources/stop-impersonation"
				className="shrink-0"
			>
				<Button
					type="submit"
					size="sm"
					variant="outline"
					className="border-warning-border text-warning-fg hover:bg-warning-border/30 bg-transparent"
				>
					{t('impersonation.stop')}
				</Button>
			</Form>
		</div>
	)
}
